import { createContext } from 'react'
import useLocalStorage from 'use-local-storage'
import {
  Client_Profile,
  ClientProfileFragment,
  SearchTestFragment,
} from '../generated/urql.client'
import {
  Consultation_Slot,
  ConsultationFragment,
  Laboratory_Test,
  Test,
  TrainingFragment,
} from '../generated/urql.anonymous'
import { WithOptional } from '../lib/types'

export type Basket = ReturnType<typeof useBasket>
export const BasketContext = createContext<Basket>({
  items: [],
  profile: undefined,
  overrideAddress: undefined,
  address: undefined,
  discountCodes: [],
  shippingData: undefined,
  addItem: () => {},
  setDiscountCodes: () => {},
  removeItem: () => {},
  changeItem: () => {},
  setProfile: () => {},
  setOverrideAddress: () => {},
  setShippingData: () => {},
  clear: () => {},
  remove: () => {},
  logout: () => {},
  onProfileUpdated: () => {},
})

export type TestBasketItem = {
  test: SearchTestFragment
  profile?: ClientProfileFragment
}

export type ShippingTestBasketItem = {
  test: SearchTestFragment & { laboratoryTests: Array<Laboratory_Test> }
  profile?: ClientProfileFragment
  shouldShipToClient?: boolean
}

export type ConsultationBasketItem = {
  consultation: ConsultationFragment
  consultationSlot?: WithOptional<Consultation_Slot, 'consultationId'>
  preferredDate?: string
  profile?: ClientProfileFragment
}

export type TrainingBasketItem = {
  training: TrainingFragment
}

export const isTestBasketItem = (
  basketItem: BasketItem
): basketItem is TestBasketItem =>
  'test' in basketItem && basketItem.test.fulfillment !== 'shipping'
export const isShippingTestBasketItem = (
  basketItem: BasketItem
): basketItem is ShippingTestBasketItem =>
  'test' in basketItem && basketItem.test.fulfillment === 'shipping'
export const isConsultationBasketItem = (
  basketItem: BasketItem
): basketItem is ConsultationBasketItem => 'consultation' in basketItem
export const isTrainingBasketItem = (
  basketItem: BasketItem
): basketItem is TrainingBasketItem => 'training' in basketItem
export const isSameBasketItem = (a: BasketItem) => (b: BasketItem) => {
  if (isTestBasketItem(a) && isTestBasketItem(b)) {
    return a.test.id === b.test.id && a.profile?.id === b.profile?.id
  }
  if (isShippingTestBasketItem(a) && isShippingTestBasketItem(b)) {
    return a.test.id === b.test.id && a.profile?.id === b.profile?.id
  }
  if (isConsultationBasketItem(a) && isConsultationBasketItem(b)) {
    return (
      a.consultation.id === b.consultation.id && a.profile?.id === b.profile?.id
    )
  }
  if (isTrainingBasketItem(a) && isTrainingBasketItem(b)) {
    return a.training.id === b.training.id
  }
  return false
}

type BasketItem =
  | TestBasketItem
  | ShippingTestBasketItem
  | ConsultationBasketItem
  | TrainingBasketItem

export type MapPoint = {
  name: string
  address: { line1: string; line2: string }
}

export type ShippingData = {
  provider: 'inpost_locker_standard'
  point?: MapPoint
  email?: string
  phone?: string
}

type StoredBasket = {
  items: BasketItem[]
  discountCodes: string[]
  profile?: ClientProfileFragment
  overrideAddress?: Pick<
    Client_Profile,
    'address' | 'postalCode' | 'city' | 'location'
  > &
    Partial<{ phoneNumber: Client_Profile['phoneNumber'] }>
  shippingData?: ShippingData
}

export function useBasket() {
  const [basket, setBasket] = useLocalStorage<StoredBasket>('basket', {
    items: [],
    discountCodes: [],
  })

  return {
    items: basket.items || [],
    profile: basket.profile,
    discountCodes: basket.discountCodes || [],
    overrideAddress: basket.overrideAddress,
    address: basket.overrideAddress || basket.profile,
    shippingData: basket.shippingData,
    setProfile: (profile: StoredBasket['profile']) => {
      setBasket({
        ...basket,
        profile,
        overrideAddress: undefined,
      })
    },

    onProfileUpdated: (profile: StoredBasket['profile']) => {
      if (basket.profile?.id === profile?.id) {
        setBasket({ ...basket, profile })
      }

      setBasket({
        ...basket,
        items: basket.items.map((i) =>
          (isTestBasketItem(i) ||
            isConsultationBasketItem(i) ||
            isShippingTestBasketItem(i)) &&
          i.profile?.id === profile?.id
            ? { ...i, profile }
            : i
        ),
      })
    },

    setDiscountCodes: (discountCodes: string[]) => {
      const b = {
        ...basket,
        discountCodes,
      }
      setBasket(b)
    },

    addItem: (item: BasketItem) => {
      if ((basket.items || []).find(isSameBasketItem(item))) {
        return
      }

      const b = { ...basket, items: [...(basket.items || []), item] }
      setBasket(b)
    },

    removeItem: (...basketItems: BasketItem[]) => {
      const b = {
        ...basket,
        items:
          basket.items.filter(
            (item) => !basketItems.find(isSameBasketItem(item))
          ) || [],
      }
      setBasket(b)
    },

    changeItem: (existingItem: BasketItem, updatedItem: BasketItem) => {
      const items = basket.items.slice()
      items.splice(
        basket.items.findIndex(isSameBasketItem(existingItem)),
        1,
        updatedItem
      )

      const b = {
        ...basket,
        items,
      }
      setBasket(b)
    },

    setOverrideAddress: (overrideAddress?: StoredBasket['overrideAddress']) => {
      setBasket({ ...basket, overrideAddress })
    },

    setShippingData: (shippingData?: ShippingData) =>
      setBasket({ ...basket, shippingData }),

    clear: () => {
      setBasket({
        ...basket,
        overrideAddress: undefined,
        items: [],
        discountCodes: [],
      })
    },

    remove: () => {
      setBasket({
        items: [],
        discountCodes: [],
      })
    },

    logout: () => {
      setBasket({
        items: [],
        discountCodes: [],
        overrideAddress: basket.overrideAddress
          ? { ...basket.overrideAddress }
          : basket.profile
          ? { ...basket.profile }
          : undefined,
      })
    },
  }
}
